/**
 * Global filters for formatting various elements, inspired by Vue2 filters
 *
 * Example:
 * <template>
 *   <div>{{ $filters.currency(10.35", "USD") }}</div>
 * </template>
 */

import { App, Plugin } from 'vue';
import date from 'date-and-time';
import { useI18n } from 'vue-i18n';

// Register global filter variable
const $filters = {
	/**
	 * Filters and formats a Date object or date string representation into a
	 * human-readable, localized date format
	 *
	 * @param value Date object or date string
	 * @param locale Locale to use for formatting the date, currently en-US/en_US and de-DE/de_DE are supported
	 * @param showDate If true, includes the date part in the formatted string
	 * @param showTime If true, includes the time part in the formatted string
	 * @returns
	 */
	dateTimeLocal(
		value: Date | string | undefined,
		{
			locale = undefined,
			showDate = true,
			showTime = true,
			showSeconds = false,
			separator = ' - ',
		}: {
			locale?: string;
			showDate?: boolean;
			showTime?: boolean;
			showSeconds?: boolean;
			separator?: string;
		} = {},
	) {
		if (typeof value === 'undefined') return '-';

		const dateValue = value instanceof Date ? value : new Date(value);
		const i18n = useI18n();

		if (isNaN(dateValue.getTime()) || dateValue.getTime() === 0) return '-';
		if (!locale) locale = i18n.locale.value;

		let dateStr = '',
			timeStr = '';
		const outStrings = [];

		// Seconds parameter
		const ss = showSeconds ? ':ss' : '';

		switch (locale) {
			case 'en-US':
			case 'en_US':
				dateStr = date.format(dateValue, 'MM/DD/YYYY');
				timeStr = date.format(dateValue, `hh:mm${ss} A`);
				break;
			case 'de-DE':
			case 'de_DE':
				dateStr = date.format(dateValue, 'DD.MM.YYYY');
				timeStr = date.format(dateValue, `HH:mm${ss}`);
				break;
			default:
				dateStr = date.format(dateValue, 'YYYY-MM-DD');
				timeStr = date.format(dateValue, `HH:mm${ss}`);
				break;
		}

		if (showDate) outStrings.push(dateStr);
		if (showTime) outStrings.push(timeStr);
		return outStrings.join(separator ?? ' - ');
	},
};

const EfFilterPlugin: Plugin = {
	install: (app: App) => {
		// Register $filters on globalProperties
		app.config.globalProperties.$appFilters = $filters;
	},
};

export default EfFilterPlugin;

declare module '@vue/runtime-core' {
	export interface ComponentCustomProperties {
		$appFilters: typeof $filters;
	}
}
